import { Koi } from '@ksyos/koi';
import type { TypedReadSchema } from '@common/formEngine/typedReadSchema.types';
import type { ProcessActivityAsset } from '@common/validation/processActivityAsset';
import type { TypedSchema } from '@common/validation/utils/typedSchemaType';
import { OsasTrialPeriod } from '@common/enums';
import { getProcessActivityAssetsSchema } from '@common/validation/processActivityAsset';
import { getTextAreaStringSchema } from '@common/validation/utils/getTextAreaStringSchema';
import type { OsasConsultExaminationReadLabel } from './osasConsultSchemas/osasConsultReadSchema';
import type {
    OsasConsultationExaminationCustomControlData,
    TherapielandProgramSelectionFields,
} from './osasConsultSchemas/types';
import type { TreatmentChoice } from './osasEvaluationSchemas/types';
import type { OsasTreatmentChoiceReadLabel } from './osasTreatmentChoice/osasTreatmentChoiceReadSchema';
import type { OsasTreatmentData } from './osasTreatmentChoice/types';
import { allTreatmentValidationFinal } from './osasAllTreatmentsSchemas/osasAllTreatmentsSchema';
import { watchPatSleepStudyPdfDataConsultSchema } from './osasCloudPatExaminationSchema';
import { osasConsultExaminationReadItems } from './osasConsultSchemas/osasConsultReadSchema';
import {
    consultDateSchema,
    osasConsultExaminationDraft,
    osasConsultExaminationFinal,
    therapielandProgramSelectionValidation,
} from './osasConsultSchemas/osasConsultSchema';
import { treatmentChoiceSchema } from './osasEvaluationSchemas/osasEvaluationSchema';
import { osasPolicyReadItems } from './osasTreatmentChoice/osasTreatmentChoiceReadSchema';
import {
    policyHasTreatmentFieldsDraft,
    policyHasTreatmentFieldsFinal,
} from './osasTreatmentChoice/osasTreatmentChoiceSchema';

export type OsasFollowUpConsultData = {
    anamnesis: string | null;
    assets: ProcessActivityAsset[];
    conclusie: string | null;
    consultDate: string;
    hasTreatment: boolean;
    isTreatmentStarted: boolean | null;
    notes: string | null;
    treatmentChoice: TreatmentChoice;
    trialPeriod: OsasTrialPeriod | null;
};

export type OsasFollowUpConsultCustomControlData = OsasFollowUpConsultData &
    OsasTreatmentData &
    TherapielandProgramSelectionFields &
    OsasConsultationExaminationCustomControlData;

type TypedOsasFollowUpConsultSchema = TypedSchema<OsasFollowUpConsultData & OsasTreatmentData>;

const osasFollowUpConsultDraft: TypedOsasFollowUpConsultSchema = {
    anamnesis: getTextAreaStringSchema(true),
    assets: getProcessActivityAssetsSchema({ max: 20 }),
    conclusie: getTextAreaStringSchema(true),
    consultDate: consultDateSchema,
    hasTreatment: Koi.boolean().allow(null),
    isTreatmentStarted: Koi.boolean().allow(null),
    notes: getTextAreaStringSchema(true),
    treatmentChoice: treatmentChoiceSchema,
    trialPeriod: Koi.koi().enum(OsasTrialPeriod).allow(null),
    ...osasConsultExaminationDraft,
    ...policyHasTreatmentFieldsDraft,
    ...therapielandProgramSelectionValidation,
};

const osasFollowUpConsultFinal: TypedOsasFollowUpConsultSchema = {
    allTreatments: allTreatmentValidationFinal,
    anamnesis: getTextAreaStringSchema(false),
    assets: getProcessActivityAssetsSchema({ max: 20 }),
    conclusie: getTextAreaStringSchema(false),
    consultDate: consultDateSchema.required(),
    hasTreatment: Koi.boolean().required(),
    isTreatmentStarted: Koi.koi().custom((value, helper) => {
        const treatmentChoice = helper.state.ancestors[0].treatmentChoice;
        if (value === null && Object.values(treatmentChoice).some((item) => !!item)) {
            return helper.error('boolean_base');
        }

        return value;
    }),
    notes: getTextAreaStringSchema(true),
    treatmentChoice: treatmentChoiceSchema,
    trialPeriod: Koi.koi().custom((value, helper) => {
        const treatmentChoice = helper.state.ancestors[0].treatmentChoice;
        if (value === null && (treatmentChoice.PAP || treatmentChoice.SPT)) {
            return helper.error('boolean_base');
        }

        return value;
    }),
    ...osasConsultExaminationFinal,
    ...policyHasTreatmentFieldsFinal,
    ...therapielandProgramSelectionValidation,
};

export const osasFollowUpConsultFinalSchema = Koi.object(osasFollowUpConsultFinal).concat(
    watchPatSleepStudyPdfDataConsultSchema
);
export const osasFollowUpConsultDraftSchema = Koi.object(osasFollowUpConsultDraft).concat(
    watchPatSleepStudyPdfDataConsultSchema
);

export type OsasFollowUpConsultReadKey = keyof OsasFollowUpConsultCustomControlData | 'taskFollowingConsultWeeks';
export type OsasFollowUpConsultReadLabel =
    | OsasConsultExaminationReadLabel
    | OsasTreatmentChoiceReadLabel
    | 'Anamnese'
    | 'Bestand(en)'
    | 'Conclusie'
    | 'Datum contact'
    | 'Notitie'
    | 'Behandeling gestart'
    | 'Proefperiode'
    | 'Taak voor de Ksyos Poli'
    | 'Nieuwe therapie starten';

export type OsasFollowUpConsultReadSectionTitle =
    | 'Conclusie'
    | 'Vervolgonderzoek'
    | 'Huidige beleid'
    | 'Nieuwe therapie starten';

export const OsasFollowUpConsultReadSchema: TypedReadSchema<
    OsasFollowUpConsultReadKey,
    OsasFollowUpConsultReadLabel
>[] = [
    {
        items: [
            { key: 'consultDate', label: 'Datum contact', type: 'dateAsString' },
            { key: 'anamnesis', label: 'Anamnese' },
        ],
    },
    {
        items: [...osasConsultExaminationReadItems, { key: 'notes', label: 'Notitie' }],
        sectionTitle: 'Vervolgonderzoek',
    },
    {
        items: [
            { key: 'conclusie', label: 'Conclusie' },
            { key: 'assets', label: 'Bestand(en)' },
        ],
        sectionTitle: 'Conclusie',
    },
    {
        items: [
            { key: 'treatmentChoice', label: 'Behandeling' },
            { key: 'isTreatmentStarted', label: 'Behandeling gestart', type: 'boolean' },
            { key: 'trialPeriod', label: 'Proefperiode' },
            { key: 'taskForPoliclinic', label: 'Taak voor de Ksyos Poli' },
        ],
        sectionTitle: 'Huidig beleid',
    },
    {
        items: [{ key: 'hasTreatment', label: 'Nieuwe therapie starten', type: 'boolean' }, ...osasPolicyReadItems],
        sectionTitle: 'Nieuwe therapie starten',
    },
];
